import { useEffect, useRef } from 'react';

import { fabric } from 'fabric';

import { BaseCanvas } from '@/components/common';
import {
  CANVAS_CONTAINER_ID,
  CANVAS_THEME_CONFIG,
  DEFAULT_CANVAS_VIEWPORT_TRANSFORM
} from '@/constants';
import {
  addDefaultFields,
  getCanvasContent,
  isVideoAsset,
  lockCanvasObjects,
  resizeCanvasFromContent
} from '@/helpers';
import { LockIcon, UnlockIcon } from '@/resources';
import { TestIds } from '@/shared/test-ids';
import { useCanvasStore } from '@/store/canvas';
import { Asset, CanvasField, CoverPhotoPayload } from '@/types';

type CoverPhotoCanvasProps = {
  fields: CanvasField[];
  containerClasses: string;
  canvasClasses: string;
  coverPhoto: CoverPhotoPayload;
  video: Asset | null;
  backgroundAsset: Asset | null;
  profilePhoto: Asset | null;
};

export const CoverPhotoCanvas = ({
  fields,
  containerClasses,
  canvasClasses,
  coverPhoto,
  video,
  backgroundAsset,
  profilePhoto
}: CoverPhotoCanvasProps) => {
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const baseCanvasSizeRef = useRef<{ width: number; height: number } | null>(null);

  const setCanvas = useCanvasStore((state) => state.setCanvas);
  const moveObjects = useCanvasStore((state) => state.moveObjects ?? false);
  const setMoveObjects = useCanvasStore((state) => state.setMoveObjects);

  const loadCanvas = (canvasJson: string | null) => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const contentJson = getCanvasContent(canvasJson, {
      backgroundAsset,
      profilePhoto
    });

    canvas.loadFromJSON(contentJson, () => {
      const viewportTransform = contentJson.viewportTransform ?? DEFAULT_CANVAS_VIEWPORT_TRANSFORM;

      baseCanvasSizeRef.current = {
        width: Math.floor((contentJson.width ?? 0) / viewportTransform[0]),
        height: Math.floor((contentJson.height ?? 0) / viewportTransform[3])
      };

      resizeCanvasFromContent(canvas, contentJson, CANVAS_CONTAINER_ID);

      if (!canvasJson) {
        addDefaultFields(canvas, fields, CANVAS_THEME_CONFIG, false);
      }

      lockCanvasObjects(canvas);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      setCanvas(canvas);
    });
  };
  const handleCanvasInit = (canvas: fabric.Canvas) => {
    canvasRef.current = canvas;
  };

  useEffect(() => {
    setMoveObjects(false);
  }, [setMoveObjects]);

  useEffect(() => {
    loadCanvas(coverPhoto?.coverPhotoJson ?? null);
  }, [coverPhoto, backgroundAsset, profilePhoto]);

  return (
    <div data-testid={TestIds.coverPhotos.canvasContainer}>
      <BaseCanvas
        containerId={CANVAS_CONTAINER_ID}
        containerClassName={`relative aspect-video rounded-2xl border border-primary-blue ${containerClasses} group`}
        canvasClassName={`rounded-2xl ${canvasClasses}`}
        baseCanvasSizeRef={baseCanvasSizeRef}
        onCanvasInit={handleCanvasInit}>
        <video
          className='absolute left-0 top-0 size-full rounded-[10px] object-cover'
          src={isVideoAsset(video) ? video?.assetUrl : ''}
          hidden={!isVideoAsset(video)}
          autoPlay
          loop
          muted
        />
        <div className='absolute left-3 top-3 z-10 flex items-center justify-center group-hover:flex md:hidden'>
          <div
            data-testid={TestIds.coverPhotos.canvasLockButton}
            className='flex size-7 cursor-pointer items-center justify-center rounded-md bg-primary-white text-primary-blue hover:bg-white md:bg-white/[0.6]'
            onClick={() => setMoveObjects(!moveObjects)}>
            {moveObjects ? <UnlockIcon /> : <LockIcon />}
          </div>
        </div>
      </BaseCanvas>
    </div>
  );
};
